import React, {useEffect, useState} from 'react';
import {StyleSheet} from 'react-native';
import {Box, HStack, Text, VStack} from 'native-base';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {StackParamList} from '../../../../../../navigators';
import InternalTemplate from '../../../../../../components/templates/internal';
import {ReadonlyInput} from '../../../../../../components/inputs';
import {CustomerIcon} from '../../../../../../components/icons';
import {PrimaryButton} from '../../../../../../components/buttons';
import {formatCurrencyValue} from '../../../../../../utils/format';
import {CurrencyOptionEnum} from '../../../../../../constants/enumerations';
import {message as t} from '../../../../../../utils/messages';
import useToast from '../../../../../../hooks';
import {SpinnerModal} from '../../../../../../components/modals';
import {useSalesmanContext} from '../../../../../../context/salesman';

type ScreenProps = NativeStackScreenProps<StackParamList, 'InvoiceSummary'>;

const InvoiceSummary = ({navigation}: ScreenProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const {invoiceCandidate, client, sendInvoiceRequest} = useSalesmanContext();
  const {showInvoiceError} = useToast();
  useEffect(() => {
    setIsLoading(false);
  }, []);

  const handleGoBack = () => {
    navigation.goBack();
  };

  const handleOnProductPress = () => {
    navigation.navigate('InvoiceAdjustableItems');
  };

  const handleOnContinuePress = async () => {
    setIsLoading(true);
    const result = await sendInvoiceRequest();
    if (result === true) {
      navigation.navigate('InvoiceSuccessFull');
    } else {
      showInvoiceError();
    }
    setIsLoading(false);
  };

  const getCurrencyLabel = () => {
    return invoiceCandidate?.currency === 'BS' ? 'Bolívares' : 'Dólares';
  };

  const getItems = () => {
    let sum = 0;
    invoiceCandidate?.items.forEach(item => {
      sum += item.count;
    });
    return sum;
  };

  const getSubTotal = () => {
    let sum = 0;
    invoiceCandidate?.items.forEach(item => {
      sum += item.count * item.price;
    });
    return sum;
  };

  const getIvaTotal = () => {
    let sum = 0;
    invoiceCandidate?.items.forEach(item => {
      sum += (item.count * item.price * item.ivaPercent) / 100;
    });
    return sum;
  };

  const getTotal = () => {
    return getSubTotal() + getIvaTotal();
  };

  const getCurrency = () => {
    if (invoiceCandidate?.currency === 'BS') {
      return CurrencyOptionEnum.local;
    }

    return CurrencyOptionEnum.foreign;
  };

  return (
    <InternalTemplate
      title={'salesman-invoice-summary-title'}
      contentPaddingX="3"
      backButton={{
        onPress: () => handleGoBack(),
        label: 'go-back',
        usage: 'external',
      }}>
      <SpinnerModal show={isLoading} title={t('sending-invoice-request')} />
      <VStack paddingX={5}>
        <ReadonlyInput
          icon={<CustomerIcon width={25} />}
          label="Cliente"
          value={client!.name}
        />
        <ReadonlyInput
          label={'Condición de Pago'}
          value={invoiceCandidate!.info.terms}
          icon={undefined}
          backgroundColor="#EFEFEF"
        />
        <ReadonlyInput
          label={'Moneda de pago'}
          value={getCurrencyLabel()}
          icon={undefined}
          backgroundColor="#EFEFEF"
        />
        <ReadonlyInput
          label={'Lista de precios'}
          value={'Lista precio #1'}
          icon={undefined}
          backgroundColor="#EFEFEF"
        />
        <VStack marginTop={2}>
          <PrimaryButton
            label="salesman-invoice-product-button"
            onPress={handleOnProductPress}
          />
        </VStack>
        <VStack style={styles.summaryBox}>
          <HStack justifyContent={'space-between'} style={styles.summaryItem}>
            <Text style={styles.summaryItemLabel} fontWeight={'extrabold'}>
              {t('salesman-order-begin-summary-items')}
            </Text>
            <Text style={styles.summaryItemValue}>{getItems()}</Text>
          </HStack>
          <HStack justifyContent={'space-between'} style={styles.summaryItem}>
            <Text style={styles.summaryItemLabel} fontWeight={'extrabold'}>
              {t('salesman-order-begin-summary-subtotal')}
            </Text>
            <Text style={styles.summaryItemValue}>
              {formatCurrencyValue(getSubTotal(), getCurrency())}
            </Text>
          </HStack>
          <HStack justifyContent={'space-between'} style={styles.summaryItem}>
            <Text style={styles.summaryItemLabel} fontWeight={'extrabold'}>
              {t('salesman-order-begin-summary-iva')}
            </Text>
            <Text style={styles.summaryItemValue}>
              {formatCurrencyValue(getIvaTotal(), getCurrency())}
            </Text>
          </HStack>
          <HStack justifyContent={'space-between'} style={styles.summaryItem}>
            <Text style={styles.summaryItemLabelTotal} fontWeight={'extrabold'}>
              {t('salesman-order-begin-summary-total')}
            </Text>
            <Text style={styles.summaryItemValue}>
              {formatCurrencyValue(getTotal(), getCurrency())}
            </Text>
          </HStack>
          <Box marginTop={3}>
            <PrimaryButton
              label="salesman-invoice-summary-continue-button"
              onPress={handleOnContinuePress}
            />
          </Box>
        </VStack>
      </VStack>
    </InternalTemplate>
  );
};

export default InvoiceSummary;

const styles = StyleSheet.create({
  summaryItem: {
    borderBottomWidth: 1,
    borderColor: '#0000001F',
    marginBottom: 7,
    marginTop: 5,
  },
  summaryItemLabel: {
    color: '#00000099',
  },
  summaryItemLabelTotal: {
    color: '#000000DE',
  },
  summaryItemValue: {
    color: '#000000DE',
  },
  summaryBox: {
    backgroundColor: '#F1F3F4',
    padding: 22,
    borderRadius: 10,
    marginTop: 30,
    marginBottom: 100,
  },
  companyIcon: {
    marginLeft: 10,
  },
});
